type FormatNumberITOptions = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

/**
 * Formatter deterministico stile IT:
 * - migliaia con "."
 * - decimali con ","
 *
 * Motivazione: su iOS/WebKit `toLocaleString('it-IT')` può risultare non stabile
 * (locale non rispettata / grouping inconsistenti). Questo formatter evita i casi
 * in cui il separatore delle migliaia "sparisce".
 */
export function formatNumberIT(
  value: number,
  options: FormatNumberITOptions = {},
): string {
  if (!Number.isFinite(value)) return value.toString();

  const min = options.minimumFractionDigits ?? 0;
  const max = options.maximumFractionDigits ?? min;

  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);

  // `toFixed` produce sempre `max` decimali (rounding deterministico)
  const fixed = abs.toFixed(max);
  let [intPart, fracPart = ''] = fixed.split('.');

  // Raggruppa migliaia con "."
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  if (max === 0) return `${sign}${intPart}`;

  // Se max > min, elimina zeri finali ma non sotto min
  if (max > min) {
    fracPart = fracPart.replace(/0+$/, '');
    if (fracPart.length < min) {
      fracPart = fracPart.padEnd(min, '0');
    }
  }

  if (fracPart.length === 0) return `${sign}${intPart}`;
  return `${sign}${intPart},${fracPart}`;
}

/**
 * Parsing robusto per numeri che possono arrivare già formattati:
 * - "10.000", "10.000,50", "€ 10.000,50"
 * - "1,234.56" (en-US)
 *
 * Ritorna `null` se non è un numero valido.
 */
function parseLooseNumber(value: string): number | null {
  const raw = value.trim();
  if (!raw) return 0;

  // Mantieni solo cifre e separatori base; rimuovi simboli (€, %, spazi, ecc.)
  let cleaned = raw.replace(/[^\d,.\-]/g, '');
  if (!cleaned) return null;

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');

  if (hasComma && hasDot) {
    // Decimale = ultimo separatore tra , e .
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    const decimalIsComma = lastComma > lastDot;

    if (decimalIsComma) {
      // IT: "." migliaia, "," decimale
      cleaned = cleaned.replace(/\./g, '').replace(/,/g, '.');
    } else {
      // EN: "," migliaia, "." decimale
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (hasComma && !hasDot) {
    // Se è in stile migliaia EN (es. 1,234), rimuovi le virgole; altrimenti virgola = decimale
    const looksLikeThousands = /^-?\d{1,3}(,\d{3})+$/.test(cleaned);
    cleaned = looksLikeThousands
      ? cleaned.replace(/,/g, '')
      : cleaned.replace(/,/g, '.');
  } else if (!hasComma && hasDot) {
    // Se è in stile migliaia IT (es. 10.000), rimuovi i punti; altrimenti punto = decimale
    const looksLikeThousands = /^-?\d{1,3}(\.\d{3})+$/.test(cleaned);
    cleaned = looksLikeThousands ? cleaned.replace(/\./g, '') : cleaned;
  }

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export const getCorrectCurrencyFormat = (value: number | string): string => {
  if (typeof value === 'string' && value.trim() === '') return '€ 0';

  const numberValue =
    typeof value === 'number' ? value : parseLooseNumber(value);

  if (numberValue === null) return value.toString();

  const hasDecimals = numberValue % 1 !== 0;
  const numericString = formatNumberIT(numberValue, {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  });

  return `€ ${numericString}`;
};

export const getCorrectPercentageFormat = (value: number | string): string => {
  const numberValue =
    typeof value === 'number' ? value : parseLooseNumber(value.toString());

  if (numberValue === null) return value.toString();

  // Alcune sorgenti forniscono il valore come ratio (0-1),
  // altre come percentuale già scalata (0-100). Evitiamo il "doppio *100".
  const normalizedValue =
    Math.abs(numberValue) <= 1 ? numberValue * 100 : numberValue;
  const formattedValue = formatNumberIT(normalizedValue, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${formattedValue} %`;
};

export const getCorrectNumberFormat = (value: number | string): string => {
  const numberValue =
    typeof value === 'number' ? value : parseLooseNumber(value.toString());

  if (numberValue === null) return value.toString();

  const hasDecimals = numberValue % 1 !== 0;
  return formatNumberIT(numberValue, {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  });
};

export const getCorrectDurationFormat = (value: number | string): string => {
  let numberValue: number;
  if (typeof value === 'string') {
    if (value.trim() === '') {
      return value;
    }
    numberValue = Number(value.replace(',', '.'));
  } else {
    numberValue = Number(value);
  }

  if (!isNaN(numberValue) && numberValue >= 0) {
    const hours = Math.floor(numberValue / 3600);
    const minutes = Math.floor((numberValue % 3600) / 60);
    const seconds = Math.floor(numberValue % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  return value.toString();
};
